import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, ClipImage } from "@/components/Motion";
import { CallButton, ConsultButton } from "@/components/Buttons";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/components/Common";
import { CONCIERGE } from "@/data/site";

export default function Concierge() {
  usePageMeta("Home Concierge Services Palm Beach County | Contractor Access & Arrival Prep | First Due", "Personalized home concierge services in western Palm Beach County: contractor access, arrival and departure preparation, post-storm checks, packages and groceries.");
  return (
    <main data-testid="concierge-page">
      <PageHero
        testId="concierge-hero"
        eyebrow="Personalized Home Concierge Services"
        lines={["More Convenience.", "Less Worry."]}
        intro="In addition to our scheduled home watch visits, we offer concierge services designed to make owning a seasonal or temporarily unoccupied home easier."
        img="/images/gate.jpg"
        alt="Gated estate entrance with palm-lined driveway"
      >
        <ConsultButton testId="concierge-hero-consult-btn" label="Ask About Concierge Services" dark />
        <CallButton testId="concierge-hero-call-btn" dark />
      </PageHero>

      <section className="bg-[#F8F7F3] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal className="flex items-start gap-3 rounded-[4px] border border-[#14263D]/15 bg-white p-5 text-sm text-[#14263D] md:max-w-2xl">
            <Info className="h-5 w-5 shrink-0 text-[#A52A32]" />
            All concierge services require homeowner authorization, are subject to scheduling and availability, and are included in your personalized written quote.
          </Reveal>
          <div className="mt-14 grid gap-14 lg:grid-cols-12">
            <div className="hidden lg:col-span-4 lg:block">
              <ClipImage src="/images/interior.jpg" alt="Living room prepared for a homeowner's return" className="sticky top-28 h-[560px] rounded-[4px]" />
            </div>
            <div className="lg:col-span-8">
              <ClipImage src="/images/interior.jpg" alt="Living room prepared for a homeowner's return" className="mb-10 h-64 rounded-[4px] lg:hidden" />
              {CONCIERGE.map((c, i) => (
                <motion.article
                  key={c.title}
                  data-testid={`concierge-item-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="group grid gap-4 border-t border-[#14263D]/15 py-8 md:grid-cols-12 md:gap-8"
                >
                  <span className="font-mono text-xs text-[#A52A32] md:col-span-1 md:pt-2">{String(i + 1).padStart(2, "0")}</span>
                  <div className="md:col-span-11">
                    <h3 className="font-display text-3xl font-semibold text-[#14263D] transition-transform duration-500 group-hover:translate-x-2">{c.title}</h3>
                    <p className="mt-3 text-base leading-relaxed text-[#5F6B76]">{c.text}</p>
                    {c.note && <p data-testid={`concierge-note-${i}`} className="mt-3 border-l-2 border-[#A52A32] pl-4 text-sm leading-relaxed text-[#14263D]">{c.note}</p>}
                  </div>
                </motion.article>
              ))}
              <Reveal className="mt-6 rounded-[4px] bg-[#14263D] p-8 text-white">
                <p className="eyebrow !text-[#f0b3b7]">Important Note</p>
                <p data-testid="concierge-important-note" className="mt-3 text-base leading-relaxed text-white/90">
                  First Due does not guarantee immediate emergency response, 24-hour staffing, storm access or licensed repair services.
                </p>
              </Reveal>
              <div className="mt-10 flex flex-wrap gap-4">
                <ConsultButton testId="ask-concierge-btn" label="Request a Personalized Quote" />
                <CallButton testId="concierge-call-btn" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTABand testId="concierge-cta" consultLabel="Ask About Concierge Services" />
    </main>
  );
}
